import type { Permission } from '../access';

/**
 * Navigation registry (docs/design/IA.md; council UX decision 2026-10-04). Every planned page keeps
 * its final route in code (IA rule 1). Whether a page is SHOWN to the user is decided by the single
 * switch `USER_VISIBLE_MODULES` below; a module that is not in it is hidden everywhere and its route
 * answers a clean 404 instead of a "not built yet" page (router.tsx). Credit and debit notes are not
 * a page: they are tabs of the billing documents list (IA rule 4).
 */
export type HubId = 'billing' | 'incoming' | 'filing' | 'records' | 'organization';

export type ModuleId =
  | 'billingDocuments'
  | 'receipts'
  | 'paymentTracking'
  | 'receivedDocuments'
  | 'filing'
  | 'filingSales'
  | 'filingPurchases'
  | 'filingVat'
  | 'filingWithholding'
  | 'filingFiles'
  | 'buyers'
  | 'suppliers'
  | 'items'
  | 'orgSetup'
  | 'team'
  | 'imports';

/**
 * The not-built ids. Every one is now a module: the credit and debit note tabs of the billing list
 * were the one non-module id, and they became real registers in WP-11.4 Slice 8.
 */
export type NotBuiltId = ModuleId;

export type ModuleRoute =
  | '/orgs/$orgId/sales/invoices'
  | '/orgs/$orgId/sales/receipts'
  | '/orgs/$orgId/sales/payments'
  | '/orgs/$orgId/incoming/documents'
  | '/orgs/$orgId/filing'
  | '/orgs/$orgId/filing/sales'
  | '/orgs/$orgId/filing/purchases'
  | '/orgs/$orgId/filing/vat'
  | '/orgs/$orgId/filing/withholding'
  | '/orgs/$orgId/filing/files'
  | '/orgs/$orgId/records/customers'
  | '/orgs/$orgId/records/suppliers'
  | '/orgs/$orgId/records/items'
  | '/orgs/$orgId/organization/setup'
  | '/orgs/$orgId/organization/team'
  | '/orgs/$orgId/organization/imports';

export interface HubModule {
  id: ModuleId;
  route: ModuleRoute;
  /** Planned block (MASTER_PARITY_PROMPT_v2 §5). */
  block: number;
  /** The page is shown to members who hold any of these keys (any-of; Block 3 plan RF-32). */
  permissions: readonly Permission[];
}

/**
 * ── THE ONE SWITCH: which modules a signed-in user may SEE ────────────────────────────────────
 * Owner rule (docs/parity/ACCEPTANCE_GATE.md §3): any feature visible to the user must work
 * end-to-end, OR be hidden when it is not complete.
 *
 * A module belongs in this set ONLY when its page is complete and works. Removing an id hides it
 * everywhere at once — the top-bar hubs, the hub page strip, the command palette and the Today
 * tiles all read this set — and its route answers a clean 404 (router.tsx `notBuilt`), never a
 * "coming soon" / "not built yet" page. The module's code (route, component, copy) stays in the
 * repository, so a later block re-enables it by adding the id back here — and changing nothing
 * else.
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 */
export const USER_VISIBLE_MODULES: ReadonlySet<ModuleId> = new Set<ModuleId>([
  // Block 1 — the billing documents register, editor and detail.
  'billingDocuments',
  // Block 8 — electronic receipts (WP-8.6), real end-to-end.
  'receipts',
]);

export const isModuleVisible = (id: ModuleId): boolean => USER_VISIBLE_MODULES.has(id);

export interface Hub {
  id: HubId;
  modules: HubModule[];
}

/** IA order: Today (a direct link, not listed here), Billing, Incoming, Filing, Records, Organization. */
export const HUBS: Hub[] = [
  {
    id: 'billing',
    modules: [
      {
        id: 'billingDocuments',
        route: '/orgs/$orgId/sales/invoices',
        block: 1,
        permissions: ['invoice.view'],
      },
      {
        id: 'receipts',
        route: '/orgs/$orgId/sales/receipts',
        block: 8,
        permissions: ['receipt.view'],
      },
      {
        id: 'paymentTracking',
        route: '/orgs/$orgId/sales/payments',
        block: 6,
        permissions: ['payment.view'],
      },
    ],
  },
  {
    id: 'incoming',
    modules: [
      {
        id: 'receivedDocuments',
        route: '/orgs/$orgId/incoming/documents',
        block: 4,
        permissions: ['purchase.view'],
      },
    ],
  },
  {
    // Block 3 plan v2, C3-00: the six Filing pages at their final routes. Period purchases and the
    // withholding statement read received documents, so they are built inside Block 4's gate.
    id: 'filing',
    modules: [
      {
        id: 'filing',
        route: '/orgs/$orgId/filing',
        block: 3,
        permissions: [
          'report.sales.view',
          'report.purchases.view',
          'report.vat.view',
          'form41.view',
          'report.vat.export',
          'form41.export',
        ],
      },
      {
        id: 'filingSales',
        route: '/orgs/$orgId/filing/sales',
        block: 3,
        permissions: ['report.sales.view'],
      },
      {
        id: 'filingPurchases',
        route: '/orgs/$orgId/filing/purchases',
        block: 4,
        permissions: ['report.purchases.view'],
      },
      {
        id: 'filingVat',
        route: '/orgs/$orgId/filing/vat',
        block: 3,
        permissions: ['report.vat.view'],
      },
      {
        id: 'filingWithholding',
        route: '/orgs/$orgId/filing/withholding',
        block: 4,
        permissions: ['form41.view'],
      },
      {
        // Any export key. The sales and purchases export keys arrive with C3-05.
        id: 'filingFiles',
        route: '/orgs/$orgId/filing/files',
        block: 3,
        permissions: ['report.vat.export', 'form41.export'],
      },
    ],
  },
  {
    id: 'records',
    modules: [
      {
        id: 'buyers',
        route: '/orgs/$orgId/records/customers',
        block: 4,
        permissions: ['customer.view'],
      },
      {
        id: 'suppliers',
        route: '/orgs/$orgId/records/suppliers',
        block: 4,
        permissions: ['supplier.view'],
      },
      {
        id: 'items',
        route: '/orgs/$orgId/records/items',
        block: 4,
        permissions: ['item.view'],
      },
    ],
  },
  {
    id: 'organization',
    modules: [
      {
        id: 'orgSetup',
        route: '/orgs/$orgId/organization/setup',
        block: 9,
        permissions: ['settings.manage'],
      },
      {
        id: 'team',
        route: '/orgs/$orgId/organization/team',
        block: 7,
        permissions: ['users.view'],
      },
      {
        id: 'imports',
        route: '/orgs/$orgId/organization/imports',
        block: 8,
        permissions: ['import.run'],
      },
    ],
  },
];

/** Planned block of every not-built module. */
export const NOT_BUILT_BLOCK: Record<NotBuiltId, number> = Object.fromEntries(
  HUBS.flatMap((h) => h.modules.map((m) => [m.id, m.block])),
) as Record<ModuleId, number>;

export function hubOfModule(id: ModuleId): HubId {
  return HUBS.find((h) => h.modules.some((m) => m.id === id))?.id ?? 'billing';
}

/**
 * True when another page of the hub lives under this page's route (the Filing overview and its
 * statements), so the page strip marks this page current only on its own address.
 */
export function isParentPage(module: HubModule, siblings: readonly HubModule[]): boolean {
  return siblings.some((other) => other !== module && other.route.startsWith(`${module.route}/`));
}
